import { Diagnostic } from '@codemirror/lint';
import { memoize } from 'lodash-es';

import {
  queryAssistanceClient,
  VerifyResponse,
} from '@dynatrace-sdk/client-query';

import { DQLEditorValidityInfo } from '../DQLEditor.js';

const severities: { [key: string]: 'error' | 'warning' | 'info' } = {
  error: 'error',
  warning: 'warning',
  info: 'info',
};

const toSeverity = (severity: string): 'error' | 'warning' | 'info' =>
  severities[severity.toLowerCase()] || 'info';

const toLinterMessage = ({ notifications }: VerifyResponse): Diagnostic[] =>
  (notifications ?? [])
    .map((notification): Diagnostic | undefined =>
      notification.syntaxPosition && notification.severity
        ? {
            from: notification.syntaxPosition.start.index,
            to: notification.syntaxPosition.end.index + 1,
            message: notification.message ?? '',
            severity: toSeverity(notification.severity),
          }
        : undefined,
    )
    .filter((x): x is Diagnostic => x !== undefined);

const transformToValidityInfo = (
  diagnostics: Diagnostic[],
): DQLEditorValidityInfo[] =>
  diagnostics.map(
    (diagnostic: Diagnostic): DQLEditorValidityInfo => ({
      ...diagnostic,
      severity: diagnostic.severity === 'hint' ? 'info' : diagnostic.severity,
    }),
  );

/**
 * Validates the syntax of the given DQL query.
 * Note that this function is memoized. A server request is executed only once per unique query.
 * @internal
 */
export const verifyQuery = memoize(
  async (query: string): Promise<VerifyResponse | undefined> => {
    try {
      return await queryAssistanceClient.queryVerify({ body: { query } });
    } catch {
      // Ignore error
    }

    return undefined;
  },
) as (query: string) => Promise<VerifyResponse | undefined>;

/**
 * Validates the syntax of the given DQL query.
 * @param content - the DQL query to validate
 * @param onValidationStart - called before the validation starts
 * @param onValidationEnd - called after validation finished
 * @internal
 */
export const dqlLinter = async (
  content: string,
  onValidationStart?: () => void,
  onValidationEnd?: (validityInfo: DQLEditorValidityInfo[]) => void,
): Promise<DQLEditorValidityInfo[]> => {
  onValidationStart?.();
  const result = await verifyQuery(content);

  const dqlErrors = result ? toLinterMessage(result) : [];
  const dqlEditorValidityInfos = transformToValidityInfo(dqlErrors);
  onValidationEnd?.(dqlEditorValidityInfos);

  return dqlEditorValidityInfos;
};
